import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  listCollections,
  createCollection,
  selectCollection,
  deleteCollection,
  type Collection,
} from "@/lib/api";

interface CollectionManagerProps {
  onCollectionSelect: (collection: Collection) => void;
}

const CollectionManager = ({ onCollectionSelect }: CollectionManagerProps) => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const loadCollections = async () => {
    try {
      const response = await listCollections();
      setCollections(response.collections);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load collections",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadCollections();
  }, []);

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) return;
    
    setIsLoading(true);
    try {
      await createCollection(newCollectionName);
      setNewCollectionName("");
      await loadCollections();
      toast({
        title: "Success",
        description: "Collection created successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create collection",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCollection = async (collection: Collection) => {
    setIsLoading(true);
    try {
      await selectCollection(collection.name);
      onCollectionSelect(collection);
      toast({
        title: "Success",
        description: `Selected collection: ${collection.name}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to select collection",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCollection = async (collection: Collection) => {
    if (!confirm(`Are you sure you want to delete collection "${collection.name}"?`)) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteCollection(collection.name);
      await loadCollections();
      toast({
        title: "Success",
        description: "Collection deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete collection",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 mb-4">
      <h2 className="text-lg font-semibold mb-4">Collection Manager</h2>
      
      <div className="flex gap-2 mb-4">
        <Input
          placeholder="New collection name"
          value={newCollectionName}
          onChange={(e) => setNewCollectionName(e.target.value)}
          disabled={isLoading}
        />
        <Button
          onClick={handleCreateCollection}
          disabled={isLoading || !newCollectionName.trim()}
        >
          Create Collection
        </Button>
      </div>

      <div className="space-y-2">
        {collections.map((collection) => (
          <div
            key={collection.name}
            className="flex items-center justify-between p-2 bg-gray-50 rounded"
          >
            <div>
              <span className="font-medium">{collection.name}</span>
              <span className="text-sm text-gray-500 ml-2">
                ({collection.count} documents)
              </span>
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSelectCollection(collection)}
                disabled={isLoading}
              >
                Select
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeleteCollection(collection)}
                disabled={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CollectionManager; 